import { useState, useRef, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { useCreateSwapMutation } from '../../../generated/graphql'
import { SwapForm, MfaView, type SwapView, type Quote } from './Swap'
import { createPasskeyClient, passkeyError } from '@valiant/ui-shared'
import { environment } from '../../../environment'
import { useAuth } from '../../../AuthProvider'

export function SwapContent({
  className,
  onSetReset,
}: {
  className?: string
  onSetReset?: (resetFn: () => void) => void
}) {
  const { auth } = useAuth()
  const [currentView, setCurrentView] = useState<SwapView>('form')
  const [mfaCode, setMfaCode] = useState<string>('')
  const [swapData, setSwapData] = useState<{
    fromMintId: string
    toMintId: string
    fromAmount: string
    selectedAddressId: string
    currentQuote: Quote | null | undefined
    selectedCompanyId: string
  } | null>(null)
  const formResetRef = useRef<(() => void) | null>(null)
  const actionAttempt = useRef(0)
  const actionSubmittingRef = useRef(false)
  const [actionSubmitting, setActionSubmitting] = useState(false)
  const [passkeysEnabled, setPasskeysEnabled] = useState(false)

  // GraphQL mutations
  const [{ fetching: createSwapLoading }, createSwap] = useCreateSwapMutation()

  const handleFormReset = (resetFn: () => void) => {
    formResetRef.current = resetFn
  }

  const invalidateAction = useCallback(() => {
    actionAttempt.current += 1
    actionSubmittingRef.current = false
    setActionSubmitting(false)
  }, [])

  const beginAction = () => {
    if (actionSubmittingRef.current) return null
    actionSubmittingRef.current = true
    setActionSubmitting(true)
    return ++actionAttempt.current
  }

  const finishAction = (attempt: number) => {
    if (attempt !== actionAttempt.current) return false
    actionSubmittingRef.current = false
    setActionSubmitting(false)
    return true
  }

  const handleInternalReset = useCallback(() => {
    invalidateAction()
    setMfaCode('')
    setSwapData(null)
    setCurrentView('form')
    formResetRef.current?.()
  }, [invalidateAction])

  useEffect(() => {
    if (onSetReset) {
      onSetReset(handleInternalReset)
    }
  }, [handleInternalReset, onSetReset])

  useEffect(() => {
    invalidateAction()
  }, [auth?.token, invalidateAction])

  useEffect(() => {
    return () => {
      actionAttempt.current += 1
      actionSubmittingRef.current = false
    }
  }, [])

  useEffect(() => {
    let active = true
    if (currentView !== 'mfa') {
      setPasskeysEnabled(false)
      return () => {
        active = false
      }
    }
    void createPasskeyClient(environment.API_URL)
      .enabled()
      .then(enabled => {
        if (active) setPasskeysEnabled(enabled)
      })
      .catch(() => {
        if (active) setPasskeysEnabled(false)
      })
    return () => {
      active = false
    }
  }, [currentView])

  const handleSwapSubmit = (data: {
    fromMintId: string
    toMintId: string
    fromAmount: string
    selectedAddressId: string
    currentQuote: Quote | null | undefined
    selectedCompanyId: string
  }) => {
    invalidateAction()
    setSwapData(data)
    setCurrentView('mfa')
  }

  const handleMfaSubmit = async () => {
    if (mfaCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code')
      return
    }

    if (!swapData) {
      toast.error('Missing swap data')
      return
    }

    const attempt = beginAction()
    if (attempt === null) return
    try {
      const result = await createSwap({
        input: {
          companyId: swapData.selectedCompanyId,
          fromMintId: swapData.fromMintId,
          toMintId: swapData.toMintId,
          fromAmount: swapData.fromAmount,
          toAmount: swapData.currentQuote?.toAmount || '0',
          whitelistedAddressId: swapData.selectedAddressId,
          mfaCode,
        },
      })
      if (attempt !== actionAttempt.current) return

      if (result.error) {
        if (result.error.message.includes('Invalid MFA code')) {
          toast.error('Invalid MFA code')
        } else {
          toast.error('Failed to submit swap request')
        }
        setMfaCode('')
        return
      }

      if (result.data?.createSwap.id) {
        toast.success('Swap request submitted successfully')
        handleInternalReset()
      } else {
        toast.error('Failed to submit swap request')
        setMfaCode('')
      }
    } finally {
      finishAction(attempt)
    }
  }

  const handlePasskeySubmit = async () => {
    if (!swapData || !auth?.token) {
      toast.error('Sign in again before using a passkey')
      return
    }
    const attempt = beginAction()
    if (attempt === null) return
    try {
      const passkeyApproval = await createPasskeyClient(environment.API_URL).action(
        auth.token,
        'createSwap',
      )
      if (attempt !== actionAttempt.current) return
      const result = await createSwap({
        input: {
          companyId: swapData.selectedCompanyId,
          fromMintId: swapData.fromMintId,
          toMintId: swapData.toMintId,
          fromAmount: swapData.fromAmount,
          toAmount: swapData.currentQuote?.toAmount || '0',
          whitelistedAddressId: swapData.selectedAddressId,
          passkeyApproval,
        },
      })
      if (attempt !== actionAttempt.current) return
      if (result.error || !result.data?.createSwap.id) throw new Error(result.error?.message)
      toast.success('Swap request submitted successfully')
      handleInternalReset()
    } catch (error) {
      if (attempt === actionAttempt.current) toast.error(passkeyError(error))
    } finally {
      finishAction(attempt)
    }
  }

  if (currentView === 'mfa') {
    return (
      <MfaView
        className={className}
        mfaCode={mfaCode}
        setMfaCode={setMfaCode}
        onSubmit={handleMfaSubmit}
        onPasskeySubmit={handlePasskeySubmit}
        isLoading={createSwapLoading || actionSubmitting}
        passkeysEnabled={passkeysEnabled}
      />
    )
  }

  return (
    <SwapForm className={className} onSwapSubmit={handleSwapSubmit} onReset={handleFormReset} />
  )
}
